/**
 * AMS App Sidebar - Main navigation for OrcaAMS
 * Collapsible sidebar with icon-only mode and hover expansion
 */
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  UserCircle,
  BarChart3,
  Mail,
  Settings,
  ChevronLeft,
  Shield,
  Activity,
  Database,
  CreditCard,
  ExternalLink,
  LogOut,
  MapPin,
  CheckSquare,
} from "lucide-react";
import { useAuth } from "@/global/auth/AuthContext";
import { useOrgAccess } from "@/global/hooks/useOrgAccess";
import { redirectToOtherAppWithToken } from "@/global/auth/ssoUtils";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
  SidebarSeparator,
  SidebarGroup,
  SidebarGroupLabel,
} from "@/components/ui/sidebar";
import { Button } from "@/global/components/ui/button";
import { cn } from "@/global/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIsSuperAdmin } from "@/global/hooks/useIsSuperAdmin";

const navigation = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    name: "Agents",
    href: "/agents",
    icon: Users,
  },
  {
    name: "Agencies",
    href: "/agencies",
    icon: Building2,
  },
  {
    name: "Clients",
    href: "/clients",
    icon: UserCircle,
  },
  {
    name: "Analytics",
    href: "/analytics",
    icon: BarChart3,
  },
  {
    name: "Emails",
    href: "/emails",
    icon: Mail,
  },
  {
    name: "Settings",
    href: "/settings",
    icon: Settings,
  },
];

const adminNavigation = [
  {
    name: "Admin Dashboard",
    href: "/admin/dashboard",
    icon: Shield,
  },
  {
    name: "Task Manager",
    href: "/admin/tasks",
    icon: CheckSquare,
  },
  {
    name: "User Management",
    href: "/admin/users",
    icon: Users,
  },
  {
    name: "Activity Logs",
    href: "/admin/activity",
    icon: Activity,
  },
  {
    name: "System Health",
    href: "/admin/system",
    icon: Database,
  },
  {
    name: "Subscriptions",
    href: "/admin/subscriptions",
    icon: CreditCard,
  },
];

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, toggleSidebar, setOpenMobile } = useSidebar();
  const isMobile = useIsMobile();
  const { isSuperAdmin } = useIsSuperAdmin();
  const { user, logout } = useAuth();
  const { canAccessFMA } = useOrgAccess();

  const handleNavClick = () => {
    // Auto-close sidebar on mobile when navigation item is clicked
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleFMARedirect = async () => {
    try {
      await redirectToOtherAppWithToken("fma", undefined, "/");
    } catch (error) {
      navigate("/login");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        "border-r border-white/10 overflow-x-hidden",
        isMobile ? "bg-transparent" : "bg-[#1A2A44]"
      )}
    >
      <SidebarHeader
        className={cn(
          "p-4",
          isMobile
            ? "bg-transparent pb-6 border-b border-white/10"
            : "bg-[#1A2A44]",
          state === "collapsed" && "flex justify-center"
        )}
      >
        {isMobile ? (
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src="/logos/Orca-logo-orange-white.png"
                alt="Orca"
                className="h-10 w-10 object-contain drop-shadow-glow"
              />
              <div className="absolute inset-0 bg-orange-400 blur-lg opacity-30"></div>
            </div>
            <div className="space-y-1">
              <h2 className="text-2xl font-semibold">
                <span className="bg-linear-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                  AMS
                </span>{" "}
                <span className="text-sm font-normal text-white">
                  from Orca
                </span>
              </h2>
              <p className="text-white/60 text-sm">
                Navigate to different sections
              </p>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              "flex items-center",
              state === "collapsed"
                ? "flex-col justify-center gap-3"
                : "flex-col items-center justify-center gap-3"
            )}
          >
            {state === "collapsed" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className="h-8 w-8 text-primary hover:text-primary hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 rotate-180" />
              </Button>
            )}
            <div className="relative">
              <img
                src="/logos/Orca-logo-orange-white.png"
                alt="Orca"
                className={cn(
                  "object-contain drop-shadow-glow",
                  state === "collapsed" ? "h-10 w-10" : "h-10"
                )}
              />
              <div className="absolute inset-0 bg-orange-400 blur-lg opacity-30"></div>
            </div>
            {state === "expanded" && (
              <div className="flex flex-col items-center">
                <span className="text-lg font-bold text-primary">
                  AMS{" "}
                  <span className="text-sm font-normal text-white">
                    from Orca
                  </span>
                </span>
              </div>
            )}
            {state === "expanded" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className="h-6 w-6 text-primary hover:text-primary hover:bg-white/10 absolute top-4 right-4"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </SidebarHeader>

      <SidebarContent
        className={cn(
          state === "expanded" ? "px-4" : "px-3",
          isMobile ? "py-6 bg-transparent" : "bg-[#1A2A44]"
        )}
      >
        <SidebarMenu className={cn("gap-2", isMobile && "gap-2")}>
          {navigation.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href !== "/dashboard" &&
                location.pathname.startsWith(item.href));

            return (
              <SidebarMenuItem key={item.name}>
                <SidebarMenuButton
                  asChild
                  tooltip={item.name}
                  className={cn(
                    "relative",
                    state === "collapsed" && "justify-center p-0",
                    isMobile &&
                      "p-4 rounded-2xl hover:bg-white/10 active:bg-white/15 active:scale-[0.98] transition-all duration-200",
                    !isMobile &&
                      (isActive
                        ? "!bg-[#2563eb]/20 !text-primary"
                        : "!text-gray-300 hover:!bg-[#2563eb]/20 hover:!text-primary")
                  )}
                >
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center gap-4 min-w-0",
                      state === "collapsed" &&
                        "justify-center w-full h-full p-2",
                      isMobile &&
                        "text-lg font-medium text-white/90 hover:text-white"
                    )}
                    onClick={handleNavClick}
                    data-tour={
                      item.href === "/analytics" ? "analytics-nav" :
                      item.href === "/emails" ? "emails-nav" :
                      undefined
                    }
                  >
                    {isMobile && (
                      <div className="w-2 h-2 rounded-full bg-linear-to-r from-orange-400 to-orange-600 opacity-60 flex-shrink-0"></div>
                    )}
                    {!isMobile && (
                      <item.icon className="h-9 w-9 text-primary flex-shrink-0" />
                    )}
                    {(state === "expanded" || isMobile) && (
                      <span className="truncate min-w-0">{item.name}</span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>

        {/* User Actions Section */}
        {user && (
          <>
            <SidebarSeparator className="my-4 bg-white/10" />
            {state === "expanded" && (
              <div className="px-2 mb-3">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {user.user_metadata?.first_name && user.user_metadata?.last_name
                    ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
                    : user.email?.split("@")[0]}
                </div>
                <div className="text-xs text-muted-foreground/70">
                  {user.email}
                </div>
              </div>
            )}
            <SidebarMenu className={cn("gap-2", isMobile && "gap-3")}>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="App Choice"
                  className={cn(
                    "cursor-pointer",
                    state === "collapsed" && "justify-center p-0",
                    isMobile &&
                      "p-4 rounded-2xl bg-white/5 hover:bg-white/10 active:bg-white/15 active:scale-[0.98] transition-all duration-200",
                    !isMobile &&
                      "!text-gray-300 hover:!bg-white/10 hover:!text-white"
                  )}
                >
                  <button
                    onClick={() => {
                      navigate("/app-choice");
                      handleNavClick();
                    }}
                    className={cn(
                      "flex items-center gap-4 w-full min-w-0",
                      state === "collapsed" &&
                        "justify-center w-full h-full p-2",
                      isMobile &&
                        "text-lg font-medium text-white/90 hover:text-white"
                    )}
                  >
                    {isMobile && (
                      <div className="w-2 h-2 rounded-full bg-linear-to-r from-orange-400 to-orange-600 opacity-60 flex-shrink-0"></div>
                    )}
                    {!isMobile && (
                      <MapPin className="h-9 w-9 text-primary flex-shrink-0" />
                    )}
                    {(state === "expanded" || isMobile) && (
                      <span className="truncate min-w-0">App Choice</span>
                    )}
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {canAccessFMA && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    tooltip="Switch to FMA"
                    className={cn(
                      "cursor-pointer",
                      state === "collapsed" && "justify-center p-0",
                      isMobile &&
                        "p-4 rounded-2xl bg-white/5 hover:bg-white/10 active:bg-white/15 active:scale-[0.98] transition-all duration-200",
                      !isMobile &&
                        "!text-gray-300 hover:!bg-white/10 hover:!text-white"
                    )}
                  >
                    <button
                      onClick={() => {
                        handleFMARedirect();
                        handleNavClick();
                      }}
                      className={cn(
                        "flex items-center gap-4 w-full min-w-0",
                        state === "collapsed" &&
                          "justify-center w-full h-full p-2",
                        isMobile &&
                          "text-lg font-medium text-white/90 hover:text-white"
                      )}
                    >
                      {isMobile && (
                        <div className="w-2 h-2 rounded-full bg-linear-to-r from-orange-400 to-orange-600 opacity-60 flex-shrink-0"></div>
                      )}
                      {!isMobile && (
                        <ExternalLink className="h-9 w-9 text-primary flex-shrink-0" />
                      )}
                      {(state === "expanded" || isMobile) && (
                        <span className="truncate min-w-0">Switch to FMA</span>
                      )}
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Log out"
                  className={cn(
                    "cursor-pointer",
                    state === "collapsed" && "justify-center p-0",
                    isMobile &&
                      "p-4 rounded-2xl bg-red-500/10 hover:bg-red-500/20 active:bg-red-500/25 active:scale-[0.98] transition-all duration-200",
                    !isMobile &&
                      "!text-red-400 hover:!bg-red-500/20 hover:!text-red-300"
                  )}
                >
                  <button
                    onClick={() => {
                      handleLogout();
                      handleNavClick();
                    }}
                    className={cn(
                      "flex items-center gap-4 w-full min-w-0",
                      state === "collapsed" &&
                        "justify-center w-full h-full p-2",
                      isMobile && "text-lg font-medium text-red-400"
                    )}
                  >
                    {isMobile && (
                      <div className="w-2 h-2 rounded-full bg-red-500 opacity-60 flex-shrink-0"></div>
                    )}
                    {!isMobile && (
                      <LogOut className="h-9 w-9 flex-shrink-0" />
                    )}
                    {(state === "expanded" || isMobile) && (
                      <span className="truncate min-w-0">Log out</span>
                    )}
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </>
        )}

        {/* Admin Section - Only visible to super admins */}
        {isSuperAdmin && (
          <>
            <SidebarSeparator className="my-4 bg-white/10" />
            {state === "expanded" && (
              <div className="text-xs font-semibold text-orange-400 uppercase tracking-wider px-2 mb-2">
                Admin
              </div>
            )}
            <SidebarMenu className="gap-2">
              {adminNavigation.map((item) => {
                const isActive =
                  location.pathname === item.href ||
                  location.pathname.startsWith(item.href);

                return (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.name}
                      className={cn(
                        "relative",
                        state === "collapsed" && "justify-center p-0",
                        isActive
                          ? "!bg-orange-500/20 !text-orange-400"
                          : "!text-gray-300 hover:!bg-orange-500/20 hover:!text-orange-400"
                      )}
                    >
                      <Link
                        to={item.href}
                        className={cn(
                          "flex items-center gap-4 min-w-0",
                          state === "collapsed" &&
                            "justify-center w-full h-full p-2"
                        )}
                        onClick={handleNavClick}
                      >
                        <item.icon className="h-9 w-9 text-primary flex-shrink-0" />
                        {state === "expanded" && <span className="truncate min-w-0">{item.name}</span>}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </>
        )}
      </SidebarContent>

      <SidebarFooter
        className={cn(
          "border-t border-white/10 p-4",
          isMobile ? "bg-transparent" : "bg-[#1A2A44]"
        )}
      >
        {state === "expanded" && (
          <div className="text-xs text-gray-400">© 2025 Orca Software</div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
